/**
 * Capa de almacenamiento de binarios (paginas de manga y portadas).
 *
 * Existe para que la Fase 3 no obligue a reescribir el lector. Hoy la
 * implementacion usa IndexedDB, que es lo unico disponible en una PWA. Al
 * envolver la app con Capacitor se anade una implementacion sobre el sistema
 * de archivos real, sin cuotas ni riesgo de purga, y solo cambia esta pieza:
 * el resto de la app habla siempre contra la interfaz `Almacen`.
 */

export interface Almacen {
  leer(clave: string): Promise<Blob | null>
  guardar(clave: string, dato: Blob): Promise<void>
  borrar(clave: string): Promise<void>
  /** Claves guardadas, opcionalmente filtradas por prefijo. */
  claves(prefijo?: string): Promise<string[]>
  /** Bytes ocupados en total. */
  ocupado(): Promise<number>
  /** Objetos sueltos (fichas de capitulos descargados, indices...). */
  leerDato<T>(clave: string): Promise<T | null>
  guardarDato<T>(clave: string, dato: T): Promise<void>
}

const NOMBRE_BD = 'yomou'
const ALMACEN = 'binarios'
const VERSION = 1

// Se mantiene una unica conexion abierta y se reutiliza. Abrir una por
// operacion costaba decenas de milisegundos cada vez, y al leer paginas de
// manga son cientos de operaciones seguidas: era el motivo de que cambiar
// de pagina en el lector tardara varios segundos.
let conexion: Promise<IDBDatabase> | null = null

function abrir(): Promise<IDBDatabase> {
  if (conexion) return conexion

  conexion = new Promise<IDBDatabase>((resolver, rechazar) => {
    const peticion = indexedDB.open(NOMBRE_BD, VERSION)
    peticion.onupgradeneeded = () => {
      const bd = peticion.result
      if (!bd.objectStoreNames.contains(ALMACEN)) {
        bd.createObjectStore(ALMACEN)
      }
    }
    peticion.onsuccess = () => {
      // Si el navegador cierra la conexion (poca memoria, otra pestana
      // actualizando la version), se descarta para reabrirla a la siguiente.
      peticion.result.onclose = () => {
        conexion = null
      }
      resolver(peticion.result)
    }
    peticion.onerror = () => {
      conexion = null
      rechazar(peticion.error)
    }
  })

  return conexion
}

async function operar<T>(
  modo: IDBTransactionMode,
  accion: (almacen: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const bd = await abrir()
  return new Promise<T>((resolver, rechazar) => {
    const transaccion = bd.transaction(ALMACEN, modo)
    const peticion = accion(transaccion.objectStore(ALMACEN))
    peticion.onsuccess = () => resolver(peticion.result)
    peticion.onerror = () => rechazar(peticion.error)
  })
}

export const almacenIndexedDB: Almacen = {
  async leer(clave) {
    const dato = await operar<Blob | undefined>('readonly', (a) => a.get(clave))
    return dato ?? null
  },

  async guardar(clave, dato) {
    await operar('readwrite', (a) => a.put(dato, clave))
  },

  async borrar(clave) {
    await operar('readwrite', (a) => a.delete(clave))
  },

  async claves(prefijo) {
    const todas = await operar<IDBValidKey[]>('readonly', (a) => a.getAllKeys())
    const texto = todas.map(String)
    return prefijo ? texto.filter((c) => c.startsWith(prefijo)) : texto
  },

  // IndexedDB guarda cualquier valor clonable, no solo blobs, asi que las
  // fichas de los capitulos descargados van al mismo almacen.
  async leerDato<T>(clave: string) {
    const dato = await operar<T | undefined>('readonly', (a) => a.get(clave))
    return dato ?? null
  },

  async guardarDato<T>(clave: string, dato: T) {
    await operar('readwrite', (a) => a.put(dato as unknown as Blob, clave))
  },

  async ocupado() {
    // navigator.storage da el total del origen de una vez, mucho mas barato
    // que recorrer todos los blobs sumando tamanos.
    if (navigator.storage?.estimate) {
      const { usage } = await navigator.storage.estimate()
      return usage ?? 0
    }
    return 0
  },
}

/** Implementacion activa. Al pasar a Capacitor solo cambia esta linea. */
export const almacen: Almacen = almacenIndexedDB

/**
 * Pide a iOS que no purgue los datos cuando quede poco espacio.
 *
 * Safari solo lo concede a webapps instaladas en la pantalla de inicio; en
 * una pestana normal devuelve false. Devuelve si quedo concedido, para poder
 * avisar al usuario de que instale la app antes de descargar media biblioteca.
 */
export async function pedirAlmacenamientoPersistente(): Promise<boolean> {
  if (!navigator.storage?.persist) return false
  if (await navigator.storage.persisted()) return true
  return navigator.storage.persist()
}

export function formatearBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const unidades = ['KB', 'MB', 'GB', 'TB']
  let valor = bytes / 1024
  let i = 0
  while (valor >= 1024 && i < unidades.length - 1) {
    valor /= 1024
    i++
  }
  return `${valor.toFixed(valor < 10 ? 1 : 0)} ${unidades[i]}`
}
